import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { sitesApi, Site } from '../../../api/sitesApi';
import { typesOfWorkV2Api, TypeOfWorkV2 } from '../../../api/typesOfWorkV2Api';
import { usersApi, User } from '../../../api/usersApi';
import { siteMembersApi, SiteMember } from '../../../api/siteMembersApi';
import { sitesV2Api } from '../../../api/sitesV2Api';
import { siteExecutorsApi, SiteExecutor } from '../../../api/siteExecutorsApi';
import { BottomSheet, HorizontalCategoryTabs, UserList } from '../index';
import ButtonList, { ButtonListItem } from '../buttons/ButtonList';
import StatusActionButton from '../buttons/StatusActionButton';
import '../forms/WorkTypeSelector.css';
import '../forms/UserSelector.css';
import './BaseFilter.css';

export interface BaseFilterState {
  startDate?: Date;
  endDate?: Date;
  siteId?: number;
  typeOfWorkId?: number;
  userId?: number;
  siteExecutorId?: number; // Для внешних исполнителей (взаимоисключающе с userId)
  paymentStatus?: 'paid' | 'unpaid'; // Клиентский фильтр по статусу оплаты
  isAdditionalWork?: boolean; // Клиентский фильтр по доп.работе
}

export interface FilterConfig {
  type: 'period' | 'site' | 'typeOfWork' | 'user' | 'paymentStatus' | 'isAdditionalWork';
  label: string;
  enabled: boolean;
}

interface BaseFilterProps {
  filters: BaseFilterState;
  onFiltersChange: (filters: BaseFilterState) => void;
  filterConfig: FilterConfig[];
  className?: string;
  scrollable?: boolean;
  maxHeight?: string;
  showLoading?: boolean;
  loadingText?: string;
  siteId?: number; // ID объекта для загрузки участников (если используется фильтр по пользователю)
  customUsersLoader?: () => Promise<User[]>; // Кастомная функция для загрузки пользователей
  /** Включить внешних исполнителей в фильтр по сотруднику (только с siteId). Загружает всех исполнителей объекта. */
  includeExternalExecutors?: boolean;
  /** ID текущего пользователя — показывает кнопку «Только мои смены» для быстрой фильтрации по сотруднику */
  currentUserId?: number;
}

const BaseFilter: React.FC<BaseFilterProps> = ({ 
  filters, 
  onFiltersChange, 
  filterConfig,
  className = '',
  scrollable = true,
  maxHeight = 'auto',
  showLoading = false,
  loadingText = 'Загрузка...',
  siteId,
  customUsersLoader,
  includeExternalExecutors = false,
  currentUserId
}) => {

  
  // Используем ref для хранения последней версии filters, чтобы избежать stale state
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);


  
  const [sites, setSites] = useState<Site[]>([]);
  const [typesOfWork, setTypesOfWork] = useState<TypeOfWorkV2[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [siteMembers, setSiteMembers] = useState<SiteMember[]>([]);
  const [siteExecutors, setSiteExecutors] = useState<SiteExecutor[]>([]);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPeriodSelector, setShowPeriodSelector] = useState(false);
  const [showWorkTypeSelector, setShowWorkTypeSelector] = useState(false);
  const [workTypeSearchQuery, setWorkTypeSearchQuery] = useState('');
  const [selectedWorkTypeCategory, setSelectedWorkTypeCategory] = useState<string | null>(null);
  const [workTypeLoading, setWorkTypeLoading] = useState(false);
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userLoading, setUserLoading] = useState(false);
  const [showPaymentStatusSelector, setShowPaymentStatusSelector] = useState(false);
  
  const filterRef = useRef<HTMLDivElement>(null);
  const justOpenedRef = useRef(false);

  // Закрытие BottomSheet на следующем тике, чтобы клик не «провалился» под окно (фантомное нажатие).
  const closeSheetNextTick = useCallback((close: () => void) => {
    setTimeout(close, 0);
  }, []);

  const loadFilterData = useCallback(async () => {
    try {
      const promises = [];
      
      // Загружаем только те данные, которые нужны для включенных фильтров
      // Типы работ загружаются отдельно для WorkTypeSelector через loadWorkTypes
      if (filterConfig.some(f => f.type === 'site' && f.enabled)) {
        promises.push(sitesApi.getAllSites());
      }
      // Пользователей не загружаем здесь, если есть customUsersLoader или siteId
      // Они будут загружены через loadUsers при необходимости
      if (filterConfig.some(f => f.type === 'user' && f.enabled) && !customUsersLoader && !siteId) {
        promises.push(usersApi.getAllUsers());
      }

      const results = await Promise.all(promises);
      let resultIndex = 0;

      if (filterConfig.some(f => f.type === 'site' && f.enabled)) {
        setSites(results[resultIndex++] as Site[]);
      }
      if (filterConfig.some(f => f.type === 'user' && f.enabled) && !customUsersLoader && !siteId) {
        setUsers(results[resultIndex++] as User[]);
      }
      
      
    } catch (error) {
      console.error('Ошибка при загрузке данных фильтров:', error);
    }
  }, [filterConfig, customUsersLoader, siteId]);

  // Загрузка типов работ для фильтра
  const loadWorkTypes = useCallback(async () => {
    try {
      setWorkTypeLoading(true);
      const data = await typesOfWorkV2Api.getTypesOfWork();
      setTypesOfWork(data);
    } catch (error) {
      console.error('Ошибка загрузки типов работ:', error);
    } finally {
      setWorkTypeLoading(false);
    }
  }, []);

  // Загрузка пользователей для фильтра
  const loadUsers = useCallback(async () => {
    try {
      setUserLoading(true);
      // Если есть кастомная функция загрузки, используем её
      if (customUsersLoader) {
        const data = await customUsersLoader();
        setUsers(data);
      }
      // Если есть siteId, загружаем участников объекта с ролями (всегда)
      else if (siteId && siteId > 0) {
        const membersData = await sitesV2Api.getSiteMembers(siteId);
        setSiteMembers(membersData);
        // Если includeExternalExecutors — дополнительно загружаем внешних исполнителей
        if (includeExternalExecutors) {
          try {
            const executorsData = await siteExecutorsApi.getSiteExecutors(siteId);
            const externalOnly = executorsData.filter(e => e.isExternal);
            setSiteExecutors(externalOnly);
          } catch (e) {
            console.warn('Ошибка загрузки внешних исполнителей:', e);
            setSiteExecutors([]);
          }
        } else {
          setSiteExecutors([]);
        }
      }
      // Иначе загружаем всех пользователей
      else {
        const data = await usersApi.getAllUsers();
        setUsers(data);
      }
    } catch (error) {
      console.error('Ошибка загрузки пользователей:', error);
    } finally {
      setUserLoading(false);
    }
  }, [siteId, customUsersLoader, includeExternalExecutors]);

  useEffect(() => {
    loadFilterData();
    // Если есть customUsersLoader, очищаем массив users, чтобы не использовать старые данные
    if (customUsersLoader) {
      setUsers([]);
    }
    // Если есть siteId и фильтр пользователя включен, загружаем участников/исполнителей заранее
    if ((siteId && siteId > 0 || (includeExternalExecutors && siteId)) && filterConfig.some(f => f.type === 'user' && f.enabled) && !customUsersLoader) {
      loadUsers();
    }
  }, [loadFilterData, customUsersLoader, siteId, filterConfig, loadUsers]);



  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (justOpenedRef.current) {
        justOpenedRef.current = false;
        return;
      }
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setActiveFilter(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleFilterClick = (filterType: string) => {
    if (showLoading) return;
    
    // Для периода используем BottomSheet напрямую
    if (filterType === 'period') {
      setShowPeriodSelector(true);
      setActiveFilter(null);
      return;
    }
    
    // Для типа работ используем BottomSheet напрямую
    if (filterType === 'typeOfWork') {
      setShowWorkTypeSelector(true);
      setActiveFilter(null);
      // Загружаем типы работ при открытии
      if (typesOfWork.length === 0) {
        loadWorkTypes();
      }
      return;
    }
    
    // Для пользователя используем BottomSheet напрямую
    if (filterType === 'user') {
      setShowUserSelector(true);
      setActiveFilter(null);
      // Загружаем пользователей при открытии
      if (customUsersLoader) {
        // Если есть кастомная функция загрузки, всегда загружаем (данные могут измениться)
        loadUsers();
      } else if ((siteId && siteId > 0) || (includeExternalExecutors && siteId)) {
        if (includeExternalExecutors ? siteExecutors.length === 0 : siteMembers.length === 0) {
          loadUsers();
        }
      } else {
        if (users.length === 0) {
          loadUsers();
        }
      }
      return;
    }
    
    // Для фильтра оплаты используем BottomSheet напрямую
    if (filterType === 'paymentStatus') {
      setShowPaymentStatusSelector(true);
      setActiveFilter(null);
      return;
    }
    
    // Фильтр доп.работы — переключатель: клик = только допы, ещё клик = снять фильтр
    if (filterType === 'isAdditionalWork') {
      const nextValue = filters.isAdditionalWork === true ? undefined : true;
      onFiltersChange({ ...filtersRef.current, isAdditionalWork: nextValue });
      setActiveFilter(null);
      return;
    }
    
    const newActiveFilter = activeFilter === filterType ? null : filterType;
    if (newActiveFilter) {
      justOpenedRef.current = true;
    }
    setActiveFilter(newActiveFilter);
  };

  const hasFilterValue = (filterType: string): boolean => {
    switch (filterType) {
      case 'period':
        return !!(filters.startDate && filters.endDate);
      case 'site':
        return !!filters.siteId;
      case 'typeOfWork':
        return filters.typeOfWorkId !== undefined;
      case 'user':
        return !!(filters.userId || filters.siteExecutorId);
      case 'paymentStatus':
        return !!filters.paymentStatus;
      case 'isAdditionalWork':
        return filters.isAdditionalWork === true;
      default:
        return false;
    }
  };

  const getFilterDisplayText = (filterType: string): string => {
    switch (filterType) {
      case 'period':
        if (filters.startDate && filters.endDate) {
          const start = filters.startDate.toLocaleDateString('ru-RU');
          const end = filters.endDate.toLocaleDateString('ru-RU');
          return `${start} - ${end}`;
        }
        return 'Период';
      case 'site':
        const site = sites.find(s => s.id === filters.siteId);
        return site ? site.name : 'Объект';
      case 'typeOfWork':
        if (filters.typeOfWorkId === 0) return 'Не указанный вид работы';
        const type = typesOfWork.find(t => t.id === filters.typeOfWorkId);
        return type ? type.name : 'Вид работ';
      case 'user':
        // Если включены внешние и выбран исполнитель
        if (filters.siteExecutorId && siteExecutors.length > 0) {
          const executor = siteExecutors.find(e => e.id === filters.siteExecutorId);
          return executor ? `${executor.displayName}${executor.isExternal ? ' (внешний)' : ''}` : 'Сотрудник';
        }
        // Если есть siteId, ищем в siteMembers, иначе в users
        if (siteId && siteMembers.length > 0) {
          const siteMember = siteMembers.find(m => m.userId === filters.userId);
          return siteMember?.userName || 'Сотрудник';
        } else {
          const user = users.find(u => u.id === filters.userId);
          return user ? user.name : 'Сотрудник';
        }
      case 'paymentStatus':
        if (filters.paymentStatus === 'paid') {
          return 'Оплачено';
        } else if (filters.paymentStatus === 'unpaid') {
          return 'Не оплачено';
        }
        return 'Оплата';
      case 'isAdditionalWork':
        return filters.isAdditionalWork === true ? 'Только допы' : 'Доп.работа';
      default:
        return '';
    }
  };

  const handleRemoveFilter = (filterType: string) => {
    const currentFilters = filtersRef.current;
    switch (filterType) {
      case 'period':
        onFiltersChange({ ...currentFilters, startDate: undefined, endDate: undefined });
        break;
      case 'site':
        onFiltersChange({ ...currentFilters, siteId: undefined });
        break;
      case 'typeOfWork':
        onFiltersChange({ ...currentFilters, typeOfWorkId: undefined });
        break;
      case 'user':
        onFiltersChange({ ...currentFilters, userId: undefined, siteExecutorId: undefined });
        break;
      case 'paymentStatus':
        onFiltersChange({ ...currentFilters, paymentStatus: undefined });
        break;
      case 'isAdditionalWork':
        onFiltersChange({ ...currentFilters, isAdditionalWork: undefined });
        break;
    }
  };

  const handleCustomPeriodClick = () => {
    const today = new Date();
    setTempStartDate(today);
    setTempEndDate(today);
    setShowDatePicker(true);
    setActiveFilter(null);
  };

  const handleDatePickerCancel = () => {
    setShowDatePicker(false);
    const today = new Date();
    setTempStartDate(today);
    setTempEndDate(today);
  };

  const handleDatePickerConfirm = () => {
    if (tempStartDate && tempEndDate) {
      onFiltersChange({ ...filtersRef.current, startDate: tempStartDate, endDate: tempEndDate });
      setShowDatePicker(false);
      const today = new Date();
      setTempStartDate(today);
      setTempEndDate(today);
    }
  };

  // Группировка объектов по статусу
  const activeSites = sites.filter(site => !site.closed);
  const closedSites = sites.filter(site => site.closed);

  // Группировка видов работ по секциям
  const groupedTypesOfWork = typesOfWork.reduce((acc, type) => {
    const section = type.section || 'Другое';
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(type);
    return acc;
  }, {} as { [key: string]: TypeOfWorkV2[] });

  // Группировка пользователей по статусу
  const activeUsers = users.filter(user => !user.closed);
  const firedUsers = users.filter(user => user.closed);

  // Стабилизируем функцию закрытия меню
  const handleCloseMenu = useCallback(() => {
    setActiveFilter(null);
  }, []);

  // Вспомогательные функции для создания ButtonList элементов
  const createButtonListItem = (
    id: string,
    label: string,
    onClick: () => void,
    icon?: string,
    variant: 'default' | 'danger' | 'success' | 'warning' = 'default'
  ): ButtonListItem => ({
    id,
    label,
    onClick,
    icon,
    variant
  });

  const createPeriodItems = (): ButtonListItem[] => [
    createButtonListItem(
      'week',
      'Неделя',
      () => {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const monday = new Date(today);
        monday.setDate(today.getDate() - mondayOffset);
        monday.setHours(0, 0, 0, 0);
        today.setHours(23, 59, 59, 999);
        onFiltersChange({ ...filtersRef.current, startDate: monday, endDate: today });
        closeSheetNextTick(() => { setActiveFilter(null); setShowPeriodSelector(false); });
      },
      'ri-calendar-week-line'
    ),
    createButtonListItem(
      'month',
      'Месяц',
      () => {
        const today = new Date();
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        firstDayOfMonth.setHours(0, 0, 0, 0);
        today.setHours(23, 59, 59, 999);
        onFiltersChange({ ...filtersRef.current, startDate: firstDayOfMonth, endDate: today });
        closeSheetNextTick(() => { setActiveFilter(null); setShowPeriodSelector(false); });
      },
      'ri-calendar-line'
    ),
    createButtonListItem(
      'year',
      'Год',
      () => {
        const today = new Date();
        const firstDayOfYear = new Date(today.getFullYear(), 0, 1);
        firstDayOfYear.setHours(0, 0, 0, 0);
        today.setHours(23, 59, 59, 999);
        onFiltersChange({ ...filtersRef.current, startDate: firstDayOfYear, endDate: today });
        closeSheetNextTick(() => { setActiveFilter(null); setShowPeriodSelector(false); });
      },
      'ri-calendar-line'
    ),
    createButtonListItem(
      'custom',
      'Период',
      () => {
        closeSheetNextTick(() => { setShowPeriodSelector(false); handleCustomPeriodClick(); });
      },
      'ri-calendar-add-line'
    )
  ];

  const createSiteItems = (): ButtonListItem[] => {
    const items: ButtonListItem[] = [
      createButtonListItem(
        'all-sites',
        'Все объекты',
        () => {
          onFiltersChange({ ...filtersRef.current, siteId: undefined });
          setActiveFilter(null);
        },
        'ri-list-line'
      )
    ];

    if (activeSites.length > 0) {
      activeSites.forEach(site => {
        items.push(createButtonListItem(
          `site-${site.id}`,
          site.name,
          () => {
            onFiltersChange({ ...filtersRef.current, siteId: site.id });
            setActiveFilter(null);
          },
          'ri-building-line'
        ));
      });
    }

    if (closedSites.length > 0) {
      closedSites.forEach(site => {
        items.push(createButtonListItem(
          `site-closed-${site.id}`,
          site.name,
          () => {
            onFiltersChange({ ...filtersRef.current, siteId: site.id });
            setActiveFilter(null);
          },
          'ri-building-line',
          'default'
        ));
      });
    }

    return items;
  };

  const createTypeOfWorkItems = (): ButtonListItem[] => {
    const items: ButtonListItem[] = [
      createButtonListItem(
        'all-types',
        'Все виды работ',
        () => {
          onFiltersChange({ ...filtersRef.current, typeOfWorkId: undefined });
          setActiveFilter(null);
        },
        'ri-list-line'
      )
    ];

    Object.entries(groupedTypesOfWork).forEach(([section, types]) => {
      types.forEach(type => {
        items.push(createButtonListItem(
          `type-${type.id}`,
          type.name,
          () => {
            onFiltersChange({ ...filtersRef.current, typeOfWorkId: type.id });
            setActiveFilter(null);
          },
          'ri-tools-line'
        ));
      });
    });

    return items;
  };

  const createUserItems = (): ButtonListItem[] => {
    const items: ButtonListItem[] = [
        createButtonListItem(
        'all-users',
        'Все сотрудники',
        () => {
          onFiltersChange({ ...filtersRef.current, userId: undefined, siteExecutorId: undefined });
          setActiveFilter(null);
        },
        'ri-group-line'
      )
    ];

    if (activeUsers.length > 0) {
      activeUsers.forEach(user => {
        items.push(createButtonListItem(
          `user-${user.id}`,
          user.name,
          () => {
            onFiltersChange({ ...filtersRef.current, userId: user.id });
            setActiveFilter(null);
          },
          'ri-user-line'
        ));
      });
    }

    if (firedUsers.length > 0) {
      firedUsers.forEach(user => {
        items.push(createButtonListItem(
          `user-fired-${user.id}`,
          user.name,
          () => {
            onFiltersChange({ ...filtersRef.current, userId: user.id });
            setActiveFilter(null);
          },
          'ri-user-unfollow-line',
          'default'
        ));
      });
    }

    return items;
  };

  const createPaymentStatusItems = (): ButtonListItem[] => [
    createButtonListItem(
      'all-payments',
      'Все',
      () => {
        onFiltersChange({ ...filtersRef.current, paymentStatus: undefined });
        closeSheetNextTick(() => setShowPaymentStatusSelector(false));
      },
      'ri-list-line'
    ),
    createButtonListItem(
      'paid',
      'Оплачено',
      () => {
        onFiltersChange({ ...filtersRef.current, paymentStatus: 'paid' });
        closeSheetNextTick(() => setShowPaymentStatusSelector(false));
      },
      'ri-checkbox-circle-line',
      'success'
    ),
    createButtonListItem(
      'unpaid',
      'Не оплачено',
      () => {
        onFiltersChange({ ...filtersRef.current, paymentStatus: 'unpaid' });
        closeSheetNextTick(() => setShowPaymentStatusSelector(false));
      },
      'ri-close-circle-line',
      'warning'
    )
  ];

  // Функция для рендера контекстного меню
  const renderContextMenu = () => {
    if (!activeFilter) {
      return null;
    }

    const getTitle = () => {
      const config = filterConfig.find(f => f.type === activeFilter);
      return config ? config.label : '';
    };

    const getContent = () => {
      switch (activeFilter) {
        case 'period':
          // Этот case больше не используется, так как используем BottomSheet
          return null;

        case 'site':
          return (
            <ButtonList
              items={createSiteItems()}
              spacing="normal"
              showDividers={true}
            />
          );

        case 'typeOfWork':
          // Этот case больше не используется, так как используем WorkTypeSelector
          return null;

        case 'user':
          // Этот case больше не используется, так как используем UserList в BottomSheet
          return null;

        default:
          return null;
      }
    };

    return (
      <BottomSheet
        isOpen={true}
        title={getTitle()}
        onClose={handleCloseMenu}
        size="auto"
      >
        {getContent()}
      </BottomSheet>
    );
  };

  // Рендер календаря для выбора периода
  const renderDatePicker = () => {
    if (!showDatePicker) return null;

    return (
      <BottomSheet
        isOpen={true}
        title="Выберите период"
        onClose={handleDatePickerCancel}
        size="medium"
        showHandle={true}
        allowSwipeToClose={true}
      >
        <div className="date-picker-content">
          <div className="date-input-group">
            <label>Начало периода:</label>
            <input
              type="date"
              value={tempStartDate ? tempStartDate.toISOString().split('T')[0] : ''}
              onChange={(e) => setTempStartDate(e.target.value ? new Date(e.target.value) : null)}
              className="form-input"
            />
          </div>
          <div className="date-input-group">
            <label>Конец периода:</label>
            <input
              type="date"
              value={tempEndDate ? tempEndDate.toISOString().split('T')[0] : ''}
              onChange={(e) => setTempEndDate(e.target.value ? new Date(e.target.value) : null)}
              className="form-input"
            />
          </div>
          <div className="form-actions">
            <StatusActionButton 
              variant="secondary"
              onClick={handleDatePickerCancel}
              fullWidth
            >
              Отмена
            </StatusActionButton>
            <StatusActionButton 
              variant="primary"
              onClick={handleDatePickerConfirm}
              disabled={!tempStartDate || !tempEndDate}
              fullWidth
            >
              Применить
            </StatusActionButton>
          </div>
        </div>
      </BottomSheet>
    );
  };

  // Мемоизируем включенные фильтры для оптимизации
  const enabledFilters = useMemo(() => 
    filterConfig.filter(f => f.enabled), 
    [filterConfig]
  );

  const showMyShiftsButton = currentUserId != null && currentUserId > 0
    && filterConfig.some(f => f.type === 'user' && f.enabled);
  const isMyShiftsActive = showMyShiftsButton
    && filters.userId === currentUserId
    && !filters.siteExecutorId;

  const handleMyShiftsToggle = () => {
    if (isMyShiftsActive) {
      onFiltersChange({ ...filtersRef.current, userId: undefined, siteExecutorId: undefined });
    } else {
      onFiltersChange({ ...filtersRef.current, userId: currentUserId, siteExecutorId: undefined });
    }
  };
  
  return (
    <div 
      className={`base-filter ${scrollable ? 'scrollable' : 'no-scroll'} ${className}`} 
      ref={filterRef}
      style={{ maxHeight }}
    >
      {/* Индикатор загрузки */}
      {showLoading && (
        <div className="filter-loading">
          <div className="filter-loading-spinner"></div>
          <span className="filter-loading-text">{loadingText}</span>
        </div>
      )}
      
      {/* Горизонтальный ряд с параметрами фильтров */}
      <div className="filter-parameters-row">
        {showMyShiftsButton && (
          <button
            type="button"
            className={`parameter-button ${isMyShiftsActive ? 'active' : ''}`}
            onClick={handleMyShiftsToggle}
            disabled={showLoading}
            aria-label="Только мои смены"
            aria-pressed={isMyShiftsActive}
            data-filter-type="myShifts"
          >
            <span className="parameter-label">Только мои смены</span>
          </button>
        )}
        {enabledFilters.map(filter => (
          <button
            key={filter.type}
            className={`parameter-button ${activeFilter === filter.type ? 'active' : ''}`}
            onClick={() => handleFilterClick(filter.type)}
            disabled={showLoading}
            aria-label={`Фильтр по ${filter.label.toLowerCase()}`}
            data-filter-type={filter.type}
          >
            <span className="parameter-label">
              {hasFilterValue(filter.type) ? getFilterDisplayText(filter.type) : filter.label}
            </span>
            {hasFilterValue(filter.type) && (
              <span
                className="parameter-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFilter(filter.type);
                }}
                title="Убрать фильтр"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRemoveFilter(filter.type);
                  }
                }}
                aria-label={`Убрать фильтр ${filter.label.toLowerCase()}`}
              >
                ×
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Рендер контекстного меню */}
      {renderContextMenu()}
      
      {/* BottomSheet для выбора периода в фильтре */}
      <BottomSheet
        isOpen={showPeriodSelector}
        onClose={() => {
          setShowPeriodSelector(false);
        }}
        title="Выберите период"
        size="medium"
        showHandle={true}
        allowSwipeToClose={true}
        allowSwipeToExpand={true}
      >
        <div className="period-selector-content">
          <ButtonList
            items={createPeriodItems()}
            spacing="normal"
            showDividers={true}
          />
        </div>
      </BottomSheet>
      
      {/* Рендер календаря */}
      {renderDatePicker()}
      
      {/* BottomSheet для выбора типа работ в фильтре */}
      <BottomSheet
        isOpen={showWorkTypeSelector}
        onClose={() => {
          setShowWorkTypeSelector(false);
          setWorkTypeSearchQuery('');
          setSelectedWorkTypeCategory(null);
        }}
        title="Выберите вид работ"
        size="large"
        showHandle={true}
        allowSwipeToClose={true}
        allowSwipeToExpand={true}
      >
        <div className="work-type-selector-content">
          {/* Поиск */}
          <div className="work-type-search">
            <div className="search-input-wrapper">
              <i className="ri-search-line search-icon"></i>
              <input
                type="text"
                placeholder="Поиск по названию или категории..."
                value={workTypeSearchQuery}
                onChange={(e) => setWorkTypeSearchQuery(e.target.value)}
                className="search-input"
              />
              {workTypeSearchQuery && (
                <button
                  className="search-clear"
                  onClick={() => setWorkTypeSearchQuery('')}
                >
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
          </div>

          {/* Фильтр по категориям */}
          {(() => {
            const categories = Array.from(new Set(typesOfWork.map(type => type.section)));
            return categories.length > 0 && (
              <HorizontalCategoryTabs
                categories={categories}
                value={selectedWorkTypeCategory}
                onChange={setSelectedWorkTypeCategory}
                showAllOption={true}
                allOptionLabel="Все категории"
                size="medium"
              />
            );
          })()}

          {/* Список типов работ */}
          <div className="work-type-list">
            {/* Кнопка "Не указанный вид работы" — смены с активностью без выбранного вида работ */}
            <div className="work-type-category-group work-type-unset-group">
              <div
                className={`work-type-card ${filters.typeOfWorkId === 0 ? 'selected' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onFiltersChange({ ...filters, typeOfWorkId: filters.typeOfWorkId === 0 ? undefined : 0 });
                  closeSheetNextTick(() => {
                    setShowWorkTypeSelector(false);
                    setWorkTypeSearchQuery('');
                    setSelectedWorkTypeCategory(null);
                  });
                }}
              >
                <div className="work-type-card-content">
                  <div className="work-type-card-name">Не указанный вид работы</div>
                </div>
                {filters.typeOfWorkId === 0 && (
                  <div className="work-type-card-check">
                    <i className="ri-check-line"></i>
                  </div>
                )}
              </div>
            </div>

            {workTypeLoading ? (
              <div className="work-type-loading">
                <i className="ri-loader-4-line ri-spin"></i>
                <span>Загрузка типов работ...</span>
              </div>
            ) : (() => {
              const filteredTypes = typesOfWork.filter(type => {
                const matchesSearch = type.name.toLowerCase().includes(workTypeSearchQuery.toLowerCase()) ||
                                   type.section.toLowerCase().includes(workTypeSearchQuery.toLowerCase());
                const matchesCategory = !selectedWorkTypeCategory || type.section === selectedWorkTypeCategory;
                return matchesSearch && matchesCategory;
              });

              const groupedTypes = filteredTypes.reduce((acc, type) => {
                if (!acc[type.section]) {
                  acc[type.section] = [];
                }
                acc[type.section].push(type);
                return acc;
              }, {} as Record<string, TypeOfWorkV2[]>);

              return Object.keys(groupedTypes).length === 0 ? (
                <div className="work-type-empty">
                  <i className="ri-search-line"></i>
                  <span>Типы работ не найдены</span>
                </div>
              ) : (
                Object.entries(groupedTypes).map(([category, types]) => (
                  <div key={category} className="work-type-category-group">
                    <div className="category-header">
                      <h4>{category}</h4>
                      <span className="category-count">{types.length}</span>
                    </div>
                    <div className="work-type-grid">
                      {types.map(type => (
                        <div
                          key={type.id}
                          className={`work-type-card ${filters.typeOfWorkId === type.id ? 'selected' : ''}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onFiltersChange({ ...filters, typeOfWorkId: filters.typeOfWorkId === type.id ? undefined : type.id });
                            closeSheetNextTick(() => {
                              setShowWorkTypeSelector(false);
                              setWorkTypeSearchQuery('');
                              setSelectedWorkTypeCategory(null);
                            });
                          }}
                        >
                          <div className="work-type-card-content">
                            <div className="work-type-card-name">{type.name}</div>
                          </div>
                          {filters.typeOfWorkId === type.id && (
                            <div className="work-type-card-check">
                              <i className="ri-check-line"></i>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              );
            })()}
          </div>
        </div>
      </BottomSheet>

      {/* BottomSheet для выбора пользователя в фильтре */}
      <BottomSheet
        isOpen={showUserSelector}
        onClose={() => {
          setShowUserSelector(false);
          setUserSearchQuery('');
        }}
        title="Выберите сотрудника"
        size="large"
        showHandle={true}
        allowSwipeToClose={true}
        allowSwipeToExpand={true}
      >
        <div className="user-selector-content">
          {/* Поиск */}
          <div className="user-search">
            <div className="search-input-wrapper">
              <i className="ri-search-line search-icon"></i>
              <input
                type="text"
                placeholder="Поиск по имени, роли или ID..."
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                className="search-input"
              />
              {userSearchQuery && (
                <button
                  className="search-clear"
                  onClick={() => setUserSearchQuery('')}
                >
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
          </div>

          {/* Список пользователей */}
          <div className="user-selector-list-container">
            {userLoading ? (
              <div className="user-loading">
                <i className="ri-loader-4-line ri-spin"></i>
                <span>Загрузка пользователей...</span>
              </div>
            ) : (() => {
              // Функция для цвета аватара
              const getAvatarColor = (userId: number): string => {
                const colors = [
                  '#4A90E2', '#50C878', '#FF6B6B', '#FFA500', 
                  '#9B59B6', '#E74C3C', '#3498DB', '#2ECC71'
                ];
                return colors[userId % colors.length];
              };

              // Обрабатываем в зависимости от типа данных
              const isSiteMemberMode = siteId && siteId > 0 && !customUsersLoader && siteMembers.length > 0;
              const hasExternalExecutors = includeExternalExecutors && siteExecutors.length > 0;
              
              // Участники объекта (userId)
              const memberItems = isSiteMemberMode ? siteMembers
                    .filter(item => {
                      if (!userSearchQuery) return true;
                      const searchLower = userSearchQuery.toLowerCase();
                      const name = item.userName || `Пользователь ${item.userId}`;
                      const matchesName = name.toLowerCase().includes(searchLower);
                      const matchesId = item.userId.toString().includes(searchLower);
                      const matchesRoles = item.roles && item.roles.some(r => 
                        r.isActive && r.roleName?.toLowerCase().includes(searchLower)
                      );
                      const matchesOldRole = item.roleName?.toLowerCase().includes(searchLower);
                      return matchesName || matchesId || matchesRoles || matchesOldRole;
                    })
                    .map(item => ({
                      userId: item.userId.toString(),
                      userName: item.userName || `Пользователь ${item.userId}`,
                      roles: item.roles?.filter(r => r.isActive).map(r => ({ 
                        id: r.id, 
                        roleName: r.roleName, 
                        roleId: r.roleId 
                      })),
                      userRole: item.roleName || 'Участник',
                      avatarUrl: item.avatarUrl,
                      avatarColor: getAvatarColor(item.userId),
                      onSelectUser: () => {
                        const newUserId = filtersRef.current?.userId === item.userId ? undefined : item.userId;
                        onFiltersChange({ ...filtersRef.current, userId: newUserId, siteExecutorId: undefined });
                        setTimeout(() => {
                          setShowUserSelector(false);
                          setUserSearchQuery('');
                        }, 80);
                      }
                    }))
                : [];

              // Внешние исполнители (siteExecutorId) — добавляем к списку участников
              const executorItems = hasExternalExecutors ? siteExecutors
                    .filter(item => {
                      if (!userSearchQuery) return true;
                      const searchLower = userSearchQuery.toLowerCase();
                      const name = item.displayName || item.userName || `Исполнитель ${item.id}`;
                      const matchesName = name.toLowerCase().includes(searchLower);
                      const matchesId = item.id.toString().includes(searchLower);
                      const matchesExternal = item.isExternal && 'внешний'.includes(searchLower);
                      return matchesName || matchesId || matchesExternal;
                    })
                    .map(item => ({
                      userId: `executor-${item.id}`,
                      userName: `${item.displayName || item.userName || `Исполнитель ${item.id}`} (внешний)`,
                      roles: undefined,
                      userRole: 'Внешний исполнитель',
                      avatarUrl: item.avatarUrl,
                      avatarColor: getAvatarColor(item.id),
                      onSelectUser: () => {
                        const newExecutorId = filtersRef.current?.siteExecutorId === item.id ? undefined : item.id;
                        onFiltersChange({ ...filtersRef.current, siteExecutorId: newExecutorId, userId: undefined });
                        setTimeout(() => {
                          setShowUserSelector(false);
                          setUserSearchQuery('');
                        }, 80);
                      }
                    }))
                : [];

              // Все пользователи (когда нет siteId)
              const usersItems = !isSiteMemberMode && !hasExternalExecutors ? users
                    .filter(item => {
                      if (!userSearchQuery) return true;
                      const searchLower = userSearchQuery.toLowerCase();
                      const name = item.name || `Пользователь ${item.id}`;
                      return name.toLowerCase().includes(searchLower) ||
                             item.id.toString().includes(searchLower);
                    })
                    .map(item => ({
                      userId: item.id.toString(),
                      userName: item.name || `Пользователь ${item.id}`,
                      userRole: undefined,
                      avatarUrl: item.avatarUrl,
                      avatarColor: getAvatarColor(item.id),
                      onSelectUser: () => {
                        const newUserId = filtersRef.current?.userId === item.id ? undefined : item.id;
                        onFiltersChange({ ...filtersRef.current, userId: newUserId, siteExecutorId: undefined });
                        setTimeout(() => {
                          setShowUserSelector(false);
                          setUserSearchQuery('');
                        }, 80);
                      }
                    }))
                : [];

              const userListUsers = isSiteMemberMode || hasExternalExecutors
                ? [...memberItems, ...executorItems]
                : usersItems;

              const filteredItems = userListUsers;

              return filteredItems.length === 0 ? (
                <div className="user-empty">
                  <i className="ri-search-line"></i>
                  <span>Пользователи не найдены</span>
                </div>
              ) : (
                <div className="user-selector-user-list-wrapper">
                  <UserList
                    users={userListUsers}
                    onAddUser={undefined}
                    onRemoveUser={undefined}
                    showRemoveButton={false}
                    canManagePermissions={false}
                    canManageMembers={false}
                    className="user-selector-user-list"
                  />
                </div>
              );
            })()}
          </div>
        </div>
      </BottomSheet>

      {/* BottomSheet для выбора статуса оплаты в фильтре */}
      <BottomSheet
        isOpen={showPaymentStatusSelector}
        onClose={() => {
          setShowPaymentStatusSelector(false);
        }}
        title="Выберите статус оплаты"
        size="medium"
        showHandle={true}
        allowSwipeToClose={true}
        allowSwipeToExpand={true}
      >
        <div className="payment-status-selector-content">
          <ButtonList
            items={createPaymentStatusItems()}
            spacing="normal"
            showDividers={true}
          />
        </div>
      </BottomSheet>
    </div>
  );
};

export default BaseFilter;
